import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import CloseIcon from '../../svg/CloseIcon';
import { sendCreatorBroadcast, fetchFanList } from '../../utils/api';
import { toast } from 'react-toastify';

interface BroadcastModalProps {
  open: boolean;
  onClose: () => void;
  creatorId: string;
  creatorName: string;
}

const BroadcastModal: React.FC<BroadcastModalProps> = ({
  open,
  onClose,
  creatorId,
  creatorName,
}) => {
  const theme = useTheme();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [fanCount, setFanCount] = useState<number | null>(null);
  const [isLoadingFanCount, setIsLoadingFanCount] = useState(false);

  const MAX_SUBJECT_LENGTH = 200;
  const MAX_MESSAGE_LENGTH = 5000;

  const subjectLength = subject.length;
  const messageLength = message.length;
  const isSubjectValid = subject.trim().length > 0 && subjectLength <= MAX_SUBJECT_LENGTH;
  const isMessageValid = message.trim().length > 0 && messageLength <= MAX_MESSAGE_LENGTH;
  const isFormValid = isSubjectValid && isMessageValid;

  useEffect(() => {
    const loadFanCount = async () => {
      if (open && creatorId) {
        setIsLoadingFanCount(true);
        try {
          const fanData = await fetchFanList(creatorId, 1);
          setFanCount(fanData.total_fans || 0);
        } catch (error) {
          console.error('Error fetching fan count:', error);
          setFanCount(0);
        } finally {
          setIsLoadingFanCount(false);
        }
      }
    };
    loadFanCount();
  }, [open, creatorId]);

  const handleClose = () => {
    if (!isSubmitting) {
      setSubject('');
      setMessage('');
      setShowConfirmation(false);
      setFanCount(null);
      setIsLoadingFanCount(false);
      onClose();
    }
  };

  const handleSendClick = () => {
    if (!isFormValid || isSubmitting) return;
    setShowConfirmation(true);
  };

  const handleConfirmSend = async () => {
    setShowConfirmation(false);
    setIsSubmitting(true);
    try {
      const response = await sendCreatorBroadcast(
        creatorId,
        subject.trim(),
        message.trim(),
        creatorName
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to send broadcast' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      toast.success('Your broadcast has been sent!');
      handleClose();
    } catch (error) {
      console.error('Error sending broadcast:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        toast.error('Network error - please check your connection and try again');
      } else {
        toast.error(error instanceof Error ? error.message : 'Oops! We couldn\'t send your broadcast. Please try again');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            m: 0,
            p: 0,
            borderRadius: 2,
            maxWidth: {
              xs: '500px',
              sm: '600px',
            },
            width: '100%',
          },
        }}
      >
      <Box
        display='flex'
        flexDirection='column'
        gap={3}
        p={3}
      >
        {/* Header */}
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <Typography variant='h6'>
            Broadcast to Fans
          </Typography>
          <IconButton
            onClick={handleClose}
            color='primary'
            disabled={isSubmitting}
            sx={{ mt: -0.5 }}
          >
            <CloseIcon
              height='16px'
              width='16px'
              stroke={theme.palette.text.primary}
              strokeWidth={2}
            />
          </IconButton>
        </Box>

        {/* Description */}
        <Typography variant='body2' color='text.secondary'>
          Send an email message to all your fans. This will be sent to all users who follow you.
        </Typography>

        {/* Subject Field */}
        <Box display='flex' flexDirection='column' gap={0.5}>
          <TextField
            label='Subject'
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            required
            disabled={isSubmitting}
            error={subjectLength > MAX_SUBJECT_LENGTH}
            helperText={
              subjectLength > MAX_SUBJECT_LENGTH
                ? `Subject must be ${MAX_SUBJECT_LENGTH} characters or less`
                : `${subjectLength}/${MAX_SUBJECT_LENGTH} characters`
            }
            inputProps={{
              maxLength: MAX_SUBJECT_LENGTH,
            }}
          />
        </Box>

        {/* Message Field */}
        <Box display='flex' flexDirection='column' gap={0.5}>
          <TextField
            label='Message'
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            required
            multiline
            rows={6}
            disabled={isSubmitting}
            error={messageLength > MAX_MESSAGE_LENGTH}
            helperText={
              messageLength > MAX_MESSAGE_LENGTH
                ? `Message must be ${MAX_MESSAGE_LENGTH} characters or less`
                : `${messageLength}/${MAX_MESSAGE_LENGTH} characters`
            }
            inputProps={{
              maxLength: MAX_MESSAGE_LENGTH,
            }}
          />
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              md: 'row',
            },
            justifyContent: 'flex-end',
            gap: 1.5,
          }}
        >
          <Button
            variant='outlined'
            color='secondary'
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleSendClick}
            disabled={!isFormValid || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {isSubmitting ? 'Sending...' : 'Send Broadcast'}
          </Button>
        </Box>
      </Box>
    </Dialog>

    {/* Confirmation Dialog */}
    <Dialog
      open={showConfirmation}
      onClose={handleCancelConfirmation}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: '400px',
        },
      }}
    >
      <DialogTitle>Confirm Broadcast</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {isLoadingFanCount ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading recipient count...</Typography>
            </Box>
          ) : (
            <>
              You are about to send a broadcast email to{' '}
              <strong>
                {fanCount === 0 ? 'no fans' : fanCount === 1 ? '1 fan' : `${fanCount} fans`}
              </strong>
              .
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  Subject: {subject}
                </Typography>
              </Box>
            </>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelConfirmation} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleConfirmSend}
          variant="contained"
          color="primary"
          disabled={isLoadingFanCount || fanCount === 0}
        >
          Confirm & Send
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default BroadcastModal;

